/**
 * BuildActivityPanel — "what the build is doing / what got built" surface.
 *
 * One box, shown under the user prompt. Structure mirrors the room's worker
 * stream box (AgentResponsePanel): a bordered, resizable, scrollable reader
 * showing the SELECTED agent's generated prompt with a usage chip, and a
 * footer LIST of the network's agents (the "Network built · N/N" roster) —
 * click a row to load that agent's prompt into the reader above.
 *
 * Driven by the reducer's ``buildActivity`` slice. LIVE (``live`` prop, during
 * ``isProcessing``): rows flip queued → writing → done and skills stream in,
 * with a spinner + active phase label. PERSISTED (revisit): the slice is
 * rehydrated from the AGENT rows (HYDRATE_BUILD) so prompts + per-agent usage
 * survive a reload — matching the room's persisted feed.
 */
import React, { useState, useCallback, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Check, Loader2, Circle, Sparkles, AlertTriangle, GripHorizontal, PauseCircle, Brain, ChevronDown, ChevronRight, ListChecks, Network } from 'lucide-react';
import { useResizable } from '@stepflow/ui-shared/hooks/useResizable';
import MermaidDiagram from '@stepflow/ui-shared/components/MermaidDiagram.jsx';
import { MODEL_ID_TO_FAMILY, MODEL_ROWS } from '@stepflow/ui-shared/services/generatedPricing';
import { formatMessageTime, formatMessageTimeFull } from '@stepflow/ui-shared/utils/formatDate.js';

const PHASE_LABEL = {
  DESIGNING: 'Designing the network',
  WRITING_PROMPTS: 'Writing agent roles',
  DRAFTING_SKILLS: 'Drafting reusable skills',
  FINALIZING: 'Finalizing',
};

// ─── Usage chip — same logic/shape as the room's AgentResponsePanel ──────────
function _fmtTokens(n) {
  const v = Number(n) || 0;
  if (v >= 1_000_000) return `${(v / 1_000_000).toFixed(1)}M`;
  if (v >= 1_000) return `${(v / 1_000).toFixed(1)}k`;
  return String(v);
}
function _modelLabel(modelId) {
  if (!modelId) return '';
  const short = String(modelId)
    .replace(/^global\.anthropic\./, '')
    .replace(/^anthropic\./, '')
    .replace(/^us\.anthropic\./, '')
    .replace(/^eu\.anthropic\./, '')
    .replace(/-\d{8}-v\d.*$/, '')
    .replace(/-/g, ' ');
  if (short) return short;
  const fam = MODEL_ID_TO_FAMILY[modelId];
  return fam ? (MODEL_ROWS[fam]?.label ?? fam) : modelId;
}

/** Short model tag for a per-lane chip: "sonnet 4 5" → "Sonnet", opus → "Opus". */
function _modelTag(modelId) {
  const s = String(modelId || '').toLowerCase();
  if (s.includes('opus')) return 'Opus';
  if (s.includes('sonnet')) return 'Sonnet';
  if (s.includes('haiku')) return 'Haiku';
  const lbl = _modelLabel(modelId);
  return lbl ? lbl.split(' ').slice(0, 2).join(' ') : '';
}

/** Compact right-aligned per-lane usage chip: ``Model · $ · pts``. Renders the
 *  status word (ready/queued/…) when there's no usage. */
function LaneUsage({ usage, fallback }) {
  if (!usage || typeof usage !== 'object') {
    return <span className="ml-auto shrink-0 text-[0.75rem] text-gray-400">{fallback}</span>;
  }
  const cost = Number(usage.estimatedCostUsd) || 0;
  const points = Number(usage.points) || 0;
  const tag = _modelTag(usage.modelId);
  return (
    <span className="ml-auto flex shrink-0 items-center gap-1 text-[0.75rem] tabular-nums text-gray-400 font-mono">
      {tag && <span className="text-gray-500">{tag}</span>}
      {cost > 0 && <span>{cost >= 0.01 ? `$${cost.toFixed(2)}` : `$${cost.toFixed(4)}`}</span>}
      {points > 0 && <span className="text-gray-500">{points.toLocaleString()} pts</span>}
    </span>
  );
}

function UsageBar({ usage }) {
  if (!usage || typeof usage !== 'object') return null;
  const inT = Number(usage.inputTokens) || 0;
  const outT = Number(usage.outputTokens) || 0;
  const cacheRead = Number(usage.cacheReadTokens) || 0;
  const cacheCreation = Number(usage.cacheCreationTokens) || 0;
  const totalIn = inT + cacheRead + cacheCreation;
  const cost = Number(usage.estimatedCostUsd) || 0;
  const points = Number(usage.points) || 0;
  if (totalIn === 0 && outT === 0 && points === 0) return null;
  const label = _modelLabel(usage.modelId);
  const breakdown = cacheRead > 0 ? ` (${_fmtTokens(cacheRead)} cached)` : '';

  const parts = [];
  if (label) parts.push(label);
  if (cost > 0) parts.push(`$${cost.toFixed(4)}`);
  parts.push(`↓${_fmtTokens(totalIn)}${breakdown} ↑${_fmtTokens(outT)}`);
  if (usage.calls > 1) parts.push(`${usage.calls} calls`);
  if (points > 0) parts.push(`${points.toLocaleString()} pts`);

  return (
    <div className="border-t border-gray-200 bg-white px-3 py-1 text-[0.75rem] tabular-nums text-gray-500 font-mono flex items-center gap-1 whitespace-nowrap overflow-x-auto">
      {parts.map((p, i) => (
        <React.Fragment key={i}>
          {i > 0 && <span className="text-gray-300">|</span>}
          <span>{p}</span>
        </React.Fragment>
      ))}
    </div>
  );
}

// ─── Footer roster rows (the list) ───────────────────────────────────────────
function AgentListRow({ row, selected, onSelect }) {
  const phase = row.phase || 'queued';
  const clickable = typeof row.prompt === 'string' && row.prompt.length > 0;
  return (
    <motion.div
      layout
      initial={{ opacity: 0, y: 4 }}
      animate={{ opacity: 1, y: 0 }}
      onClick={clickable ? () => onSelect(row.agentId) : undefined}
      className={`flex items-center gap-2 rounded px-1.5 py-1 ${clickable ? 'cursor-pointer hover:bg-gray-50' : ''} ${selected ? 'bg-indigo-50' : ''}`}
      title={clickable ? 'View this agent’s prompt' : undefined}
    >
      <span className="flex h-4 w-4 shrink-0 items-center justify-center">
        {phase === 'done' && !row.error ? (
          <Check className="h-4 w-4 text-emerald-500" aria-hidden />
        ) : phase === 'done' && row.error ? (
          <AlertTriangle className="h-4 w-4 text-amber-500" aria-hidden />
        ) : phase === 'writing' ? (
          <Loader2 className="h-4 w-4 animate-spin text-blue-500" aria-hidden />
        ) : (
          <Circle className="h-2 w-2 text-gray-300" aria-hidden />
        )}
      </span>
      <span className={`truncate text-[0.875rem] ${selected ? 'font-medium text-gray-900' : phase === 'done' ? 'text-gray-700' : phase === 'writing' ? 'text-gray-900 font-medium' : 'text-gray-400'}`}>
        {row.label || row.agentId}
      </span>
      <LaneUsage
        usage={phase === 'done' && !row.error ? row.usage : null}
        fallback={phase === 'writing' ? 'writing…' : phase === 'done' ? (row.error ? 'skipped' : 'ready') : 'queued'}
      />
    </motion.div>
  );
}

function SkillRow({ row, selected, onSelect }) {
  const phase = row.phase || 'queued';
  const done = phase === 'done';
  const clickable = typeof row.instructions === 'string' && row.instructions.length > 0;
  return (
    <motion.div
      layout
      initial={{ opacity: 0, y: 4 }}
      animate={{ opacity: 1, y: 0 }}
      onClick={clickable ? () => onSelect(row.name) : undefined}
      className={`flex items-center gap-2 rounded px-1.5 py-1 ${clickable ? 'cursor-pointer hover:bg-gray-50' : ''} ${selected ? 'bg-indigo-50' : ''}`}
      title={clickable ? 'View this skill' : (row.whenToUse || undefined)}
    >
      <span className="flex h-4 w-4 shrink-0 items-center justify-center">
        {done && !row.error ? (
          <Check className="h-4 w-4 text-violet-500" aria-hidden />
        ) : done && row.error ? (
          <AlertTriangle className="h-4 w-4 text-amber-500" aria-hidden />
        ) : phase === 'drafting' ? (
          <Loader2 className="h-4 w-4 animate-spin text-violet-400" aria-hidden />
        ) : (
          <Circle className="h-2 w-2 text-gray-300" aria-hidden />
        )}
      </span>
      <span className={`truncate text-[0.875rem] ${selected ? 'font-medium text-gray-900' : done ? 'text-gray-700' : phase === 'drafting' ? 'text-gray-900 font-medium' : 'text-gray-400'}`}>{row.name}</span>
      {/* Each skill now has its OWN body-draft call → its own cost chip (parallel
          SkillFactory). Falls back to status word while queued/drafting. */}
      <LaneUsage
        usage={done && !row.error ? row.usage : null}
        fallback={phase === 'drafting' ? 'drafting…' : done ? (row.error ? 'skipped' : 'added') : 'queued'}
      />
    </motion.div>
  );
}

// What the plan section is called, and what its rows hold: a legacy network's Playbook; the SUGGESTED plan
// (owner 2026-10-21 — a starting point every room may use, change or replace); an expert network's Teams.
const PLAN_KIND = {
  playbook: { title: 'Playbook', note: '', unit: 'task' },
  suggested: { title: 'Suggested plan', note: '· rooms may use it, change it or start fresh', unit: 'task' },
  teams: { title: 'Teams', note: '· suggested — every room adjusts its own', unit: 'responsibility' },
};
const _plural = (n, unit) => `${n} ${unit === 'responsibility' ? (n === 1 ? 'responsibility' : 'responsibilities')
  : (n === 1 ? unit : `${unit}s`)}`;

function PlaybookRow({ row, expanded, onToggle, unit = 'task' }) {
  // A milestone is a TREE PARENT: its groomed task-types are children. queued → planning → done;
  // each milestone is its OWN LLM call (per-milestone factory) so it carries its OWN cost chip.
  // Children render nested + indented when ``expanded`` (streaming shows them; a done build folds
  // them so only milestone rows remain — the user re-expands via the chevron).
  const phase = row.phase || 'queued';
  const done = phase === 'done';
  const count = Number(row.taskCount) || 0;
  const children = Array.isArray(row.tasks) ? row.tasks : [];
  const canExpand = children.length > 0;
  return (
    <div>
      <motion.div
        layout
        initial={{ opacity: 0, y: 4 }}
        animate={{ opacity: 1, y: 0 }}
        onClick={canExpand ? onToggle : undefined}
        className={`flex items-center gap-1.5 rounded px-1.5 py-1 ${canExpand ? 'cursor-pointer hover:bg-gray-50' : ''}`}
        title={row.exitCriterion || undefined}
      >
        {/* Chevron only when there are children to reveal; keeps alignment otherwise. */}
        <span className="flex h-3.5 w-3.5 shrink-0 items-center justify-center text-gray-400">
          {canExpand ? (
            expanded ? <ChevronDown className="h-3.5 w-3.5" aria-hidden /> : <ChevronRight className="h-3.5 w-3.5" aria-hidden />
          ) : null}
        </span>
        <span className="flex h-4 w-4 shrink-0 items-center justify-center">
          {done && !row.error ? (
            <Check className="h-4 w-4 text-sky-500" aria-hidden />
          ) : done && row.error ? (
            <AlertTriangle className="h-4 w-4 text-amber-500" aria-hidden />
          ) : phase === 'planning' ? (
            <Loader2 className="h-4 w-4 animate-spin text-sky-400" aria-hidden />
          ) : (
            <Circle className="h-2 w-2 text-gray-300" aria-hidden />
          )}
        </span>
        <span className={`truncate text-[0.875rem] ${done ? 'text-gray-700' : phase === 'planning' ? 'text-gray-900 font-medium' : 'text-gray-400'}`}>
          {row.milestoneName}
          {done && !row.error && count > 0 ? (
            <span className="ml-1 text-[0.75rem] text-gray-400">{_plural(count, unit)}</span>
          ) : null}
        </span>
        <LaneUsage
          usage={done && !row.error ? row.usage : null}
          fallback={phase === 'planning' ? 'planning…' : done ? (row.error ? 'skipped' : 'planned') : 'queued'}
        />
      </motion.div>
      {/* Children — the milestone's groomed task-types (title + owning agent), indented. */}
      <AnimatePresence initial={false}>
        {expanded && canExpand && (
          <motion.div
            layout
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            className="ml-[1.35rem] overflow-hidden border-l border-gray-200 pl-2"
          >
            {children.map((t, i) => (
              <div key={`${t.title}-${i}`} className="flex items-center gap-1.5 py-0.5">
                <Circle className="h-1.5 w-1.5 shrink-0 text-gray-300" aria-hidden />
                <span className="truncate text-[0.8125rem] text-gray-600">{t.title}</span>
                {t.leadAgent ? (
                  <span className="shrink-0 text-[0.75rem] text-gray-400">{t.leadAgent}</span>
                ) : null}
              </div>
            ))}
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

/**
 * useCollapseOnDone — FOCUS/ACCORDION collapse for a build section: open while a section is active,
 * auto-collapse the moment it FINISHES (``done`` false→true), auto-reopen if it restarts
 * (true→false). Collapse fires ONLY on the transition, so a manual toggle between transitions sticks
 * (never fight the user). ``done`` is the section's completion signal (reasoning: stream ended;
 * agents/skills: all rows phase==='done' — AppSync-fed; prompt: build moved past kickoff). A dropped
 * done-event just leaves a section open — cosmetic, self-heals on revisit (persisted rows carry done).
 */
function useCollapseOnDone(done) {
  const [open, setOpen] = useState(!done);
  const prev = React.useRef(done);
  useEffect(() => {
    if (done && !prev.current) setOpen(false);      // just finished → fold
    else if (!done && prev.current) setOpen(true);  // restarted → reopen
    prev.current = done;
  }, [done]);
  return [open, setOpen];
}

/**
 * ReasoningBlock — the architect's live/persisted thought stream. While ``streaming``
 * it auto-expands and pins the scroll to the newest text (so the user watches it think);
 * once settled it's collapsible and defaults collapsed on revisit.
 */
function ReasoningBlock({ text, streaming }) {
  // Open while streaming; AUTO-COLLAPSE the moment the stream ends (done = !streaming). Manual
  // toggle still works and sticks (useCollapseOnDone only acts on the transition).
  const [open, setOpen] = useCollapseOnDone(!streaming);
  const bodyRef = React.useRef(null);
  // Follow the stream: keep the newest text in view while it's still arriving.
  useEffect(() => {
    if (streaming && open && bodyRef.current) {
      bodyRef.current.scrollTop = bodyRef.current.scrollHeight;
    }
  }, [text, streaming, open]);

  return (
    <div className="border-b border-gray-200 bg-white">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="flex w-full items-center gap-1.5 px-3 py-1.5 text-left"
        data-testid="build-reasoning-toggle"
      >
        {open ? <ChevronDown className="h-3.5 w-3.5 text-gray-400" /> : <ChevronRight className="h-3.5 w-3.5 text-gray-400" />}
        <Brain className={`h-4 w-4 ${streaming ? 'text-blue-500' : 'text-gray-400'}`} aria-hidden />
        <span className="text-[0.75rem] font-medium uppercase tracking-wide text-gray-400">
          Architect reasoning
        </span>
        {streaming && <Loader2 className="h-3.5 w-3.5 animate-spin text-blue-500" aria-hidden />}
      </button>
      {open ? (
        <div
          ref={bodyRef}
          className="max-h-48 overflow-y-auto whitespace-pre-wrap px-3 pb-2 font-mono text-[0.8125rem] leading-relaxed text-gray-600 [scrollbar-gutter:stable]"
          data-testid="build-reasoning-body"
        >
          {text}
        </div>
      ) : null}
    </div>
  );
}

export default function BuildActivityPanel({
  buildActivity, live = true, awaitingApproval = false, prompt = null,
  networkId = null, builds = null, planKind = 'playbook', teamsDiagram = '',
}) {
  const plan = PLAN_KIND[planKind] || PLAN_KIND.playbook;
  // ── Revision switcher ──────────────────────────────────────────────────
  // ``builds`` (newest first, from GET /networks/{id}) lists every revision.
  // Selecting an older one fetches its immutable manifest and overrides the
  // live/latest slice. A live build always shows the current slice (no override).
  const revisions = Array.isArray(builds) ? builds : [];
  const [revOverride, setRevOverride] = useState(null); // {taskId, agents{}, skills{}, prompt}
  const [revLoading, setRevLoading] = useState(false);
  // SHIP ARCHITECTURE (PLAN-DIAGRAMS.md): the authored project diagram, keyed by build taskId. From
  // the built manifest (targetArchitecture / playbook.targetDiagram); '' when the network has none.
  const [shipByTask, setShipByTask] = useState({});   // { taskId: mermaidSrc }

  const _shipFromManifest = (m) => (
    (m && m.targetArchitecture && typeof m.targetArchitecture.diagram === 'string'
      && m.targetArchitecture.diagram)
    || (m && m.playbook && typeof m.playbook.targetDiagram === 'string' && m.playbook.targetDiagram)
    || '');

  const latestTaskId = buildActivity?.taskId || (revisions[0] && revisions[0].taskId) || null;
  const activeTaskId = revOverride?.taskId || latestTaskId;

  const selectRevision = useCallback(async (taskId) => {
    if (!networkId || !taskId) return;
    if (taskId === latestTaskId) { setRevOverride(null); return; }
    setRevLoading(true);
    try {
      const { getBuildManifest } = await import('@stepflow/ui-shared/services/networkExecutionsApi');
      const m = await getBuildManifest(networkId, taskId);
      if (!m) { setRevOverride(null); return; }
      const _ship = _shipFromManifest(m);
      if (_ship) setShipByTask((prev) => (prev[taskId] ? prev : { ...prev, [taskId]: _ship }));
      const aRows = {};
      for (const a of (Array.isArray(m.agents) ? m.agents : [])) {
        if (!a || !a.logicalId || !a.systemPrompt) continue;
        aRows[a.logicalId] = { agentId: a.logicalId, label: a.logicalId, phase: 'done', prompt: a.systemPrompt, ...(a.usage ? { usage: a.usage } : {}) };
      }
      const sRows = {};
      for (const s of (Array.isArray(m.skills) ? m.skills : [])) {
        if (!s || !s.name) continue;
        sRows[s.name] = { name: s.name, phase: 'done', ...(s.skillId ? { skillId: s.skillId } : {}), ...(s.whenToUse ? { whenToUse: s.whenToUse } : {}), ...(s.instructions ? { instructions: s.instructions } : {}), ...(s.usage ? { usage: s.usage } : {}) };
      }
      setRevOverride({
        taskId, agents: aRows, skills: sRows,
        prompt: typeof m.prompt === 'string' ? m.prompt : null,
        architectUsage: (m.architectUsage && typeof m.architectUsage === 'object') ? m.architectUsage : null,
        // The versioned reasoning for THIS revision (from builds/{taskId}.json) — so the
        // reasoning block follows the selected revision, not just the live stream.
        reasoning: typeof m.architectReasoning === 'string' ? m.architectReasoning : '',
        reasoningDone: true,
      });
    } finally {
      setRevLoading(false);
    }
  }, [networkId, latestTaskId]);

  // The slice actually rendered: an older revision's manifest, else the live/latest.
  const ba = revOverride || buildActivity || {};
  // STABLE ORDER: sort by a fixed key (agentId / skill name) so rows never visually
  // jump/reshuffle as concurrent BUILD_*_ROW events (DistributedMap fires many at once)
  // mutate the dict. Deterministic + independent of arrival order.
  const _byId = (a, b) => String(a.agentId || a.name || '').localeCompare(String(b.agentId || b.name || ''));
  const agents = ba.agents ? Object.values(ba.agents).sort(_byId) : [];
  const skills = ba.skills ? Object.values(ba.skills).sort(_byId) : [];
  // FOCUS/ACCORDION (UI): each lane auto-folds when IT completes, keeping attention on what's still
  // streaming. Section-done is derived from the AppSync-fed per-row phase (all rows done); the prompt
  // folds once the build moves past kickoff (reasoning started). All still manually toggleable.
  const agentsDone = agents.length > 0 && agents.every((r) => String(r.phase || '') === 'done');
  const skillsDone = skills.length > 0 && skills.every((r) => String(r.phase || '') === 'done');
  const promptSettled = !!(ba.reasoning && ba.reasoning.length > 0);   // reasoning began → fold prompt
  const [promptOpen, setPromptOpen] = useCollapseOnDone(promptSettled);
  // Reasoning is SETTLED when its stream's done flag is set OR the build has visibly moved past the
  // design/approval gate (agents/skills/playbook rows exist). The latter is the RELIABLE signal — it
  // survives a hard refresh (those rows persist + replay), whereas the one-shot BUILD_REASONING 'done'
  // event can be missed on replay, leaving the section falsely "streaming" and never collapsing.
  const reasoningSettled = !!(
    ba.reasoningDone || agents.length > 0 || skills.length > 0
    || Object.keys(ba.playbook || {}).length > 0
  );
  const [agentsOpen, setAgentsOpen] = useCollapseOnDone(agentsDone);
  const [skillsOpen, setSkillsOpen] = useCollapseOnDone(skillsDone);
  // Playbook (project networks): one row per milestone, ordered by milestoneId (M0, M1, …).
  const playbook = ba.playbook
    ? Object.values(ba.playbook).sort((a, b) =>
        String(a.milestoneId || '').localeCompare(String(b.milestoneId || '')))
    : [];

  // SHIP ARCHITECTURE (PLAN-DIAGRAMS.md): the authored project diagram for the ACTIVE build. On a
  // settled build (design done / awaiting approval) with no revision override, fetch the built
  // manifest once to get it — the live streaming slice doesn't carry the diagram. Keyed by taskId so
  // switching revisions shows the right one; a network with none renders nothing (no-op).
  // An EXPERT network's architecture is its teams, drawn from the LIVE rulebook (the network GET) — it wins over the
  // copy saved in the latest build's manifest; an older revision still shows what that build drew.
  const activeShip = (!revOverride && teamsDiagram) || shipByTask[activeTaskId] || '';
  const _buildSettled = awaitingApproval || agentsDone || playbook.length > 0;
  useEffect(() => {
    if (revOverride || !networkId || !latestTaskId || !_buildSettled) return;
    if (shipByTask[latestTaskId] !== undefined) return;   // fetched (even if empty) — don't refetch
    let cancelled = false;
    (async () => {
      try {
        const { getBuildManifest } = await import('@stepflow/ui-shared/services/networkExecutionsApi');
        const m = await getBuildManifest(networkId, latestTaskId);
        if (!cancelled) setShipByTask((prev) => ({ ...prev, [latestTaskId]: _shipFromManifest(m) }));
      } catch { if (!cancelled) setShipByTask((prev) => ({ ...prev, [latestTaskId]: '' })); }
    })();
    return () => { cancelled = true; };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [networkId, latestTaskId, _buildSettled, revOverride]);
  const [archOpen, setArchOpen] = useState(true);        // the ship: open by default (the headline)
  // ASK #1: when the ship diagram exists, fold the Playbook section (the diagram is the headline;
  // milestones are a click away). One-shot auto-collapse on the ship appearing; user can re-open.
  const [playbookOpen, setPlaybookOpen] = useState(true);
  const _pbFoldedRef = React.useRef(false);
  useEffect(() => {
    if (activeShip && !_pbFoldedRef.current) { _pbFoldedRef.current = true; setPlaybookOpen(false); }
    if (!activeShip) _pbFoldedRef.current = false;
  }, [activeShip]);
  // PLAYBOOK TREE: milestones expand to show their groomed task-types WHILE streaming, then AUTO-
  // COLLAPSE to milestone-only rows once every milestone is done (the user re-expands via chevron).
  // ``playbookAllDone`` drives the one-time auto-fold; ``playbookExpand`` holds per-milestone
  // user overrides (id → bool) so a manual expand/collapse sticks against the auto behavior.
  const playbookAllDone = playbook.length > 0 && playbook.every((r) => r.phase === 'done');
  const [playbookExpand, setPlaybookExpand] = useState({});
  const _foldedRef = React.useRef(false);
  useEffect(() => {
    // On the transition to all-done, clear user overrides ONCE so every milestone folds shut.
    if (playbookAllDone && !_foldedRef.current) {
      _foldedRef.current = true;
      setPlaybookExpand({});
    }
    if (!playbookAllDone) _foldedRef.current = false;  // a new build re-arms the auto-fold
  }, [playbookAllDone]);
  const isMilestoneExpanded = (r) => {
    if (r.milestoneId in playbookExpand) return playbookExpand[r.milestoneId];  // user override
    return !playbookAllDone;   // default: expanded while streaming, collapsed once all done
  };
  const toggleMilestone = (id) =>
    setPlaybookExpand((prev) => ({
      ...prev,
      [id]: !(id in prev ? prev[id] : !playbookAllDone),
    }));
  const architectUsage = ba.architectUsage || null;
  // Skills cost is ONE draft call — aggregate across rows into a group total
  // (model from the row that carries usage; sum cost + points defensively).
  const skillsUsage = (() => {
    const withUsage = skills.filter((s) => s.usage && typeof s.usage === 'object');
    if (withUsage.length === 0) return null;
    let cost = 0; let points = 0; let modelId = null;
    for (const s of withUsage) {
      cost += Number(s.usage.estimatedCostUsd) || 0;
      points += Number(s.usage.points) || 0;
      if (!modelId) modelId = s.usage.modelId;
    }
    return { modelId, estimatedCostUsd: cost, points };
  })();
  // Prompt: the override's stored prompt, else the live chat message.
  const promptTextRaw = revOverride
    ? revOverride.prompt
    : (ba.prompt || (prompt && prompt.content) || null);
  // Display-only whitespace tidy: ``whitespace-pre-wrap`` renders every newline
  // verbatim, so a prompt that arrived with 3-4 consecutive newlines shows a
  // huge gap between paragraphs. Collapse any run of blank lines to a SINGLE
  // blank line (2 newlines) + trim trailing space per line. Cosmetic only —
  // does NOT change the stored prompt or what was sent to the Brain.
  const promptText = typeof promptTextRaw === 'string'
    ? promptTextRaw.replace(/[ \t]+\n/g, '\n').replace(/\n{3,}/g, '\n\n')
    : promptTextRaw;
  const promptTime = revOverride ? null : (prompt && prompt.timestamp) || null;

  // Reader box height — same draggable mechanic as the room's worker box.
  const { height, onMouseDown } = useResizable({
    minHeight: 60, defaultHeight: 150,
    maxHeight: typeof window !== 'undefined' ? window.innerHeight * 0.6 : 400,
  });

  // What's loaded in the reader: an agent prompt OR a skill body. ``{kind,id}``.
  const [sel, setSel] = useState(null);
  const selectAgent = useCallback((id) => setSel({ kind: 'agent', id }), []);
  const selectSkill = useCallback((name) => setSel({ kind: 'skill', id: name }), []);
  // Auto-select: the agent being written live, else the first agent w/ a prompt.
  useEffect(() => {
    const writing = agents.find((a) => a.phase === 'writing' && a.prompt);
    const firstAgent = agents.find((a) => a.prompt);
    setSel((cur) => {
      if (cur && cur.kind === 'agent' && agents.some((a) => a.agentId === cur.id && a.prompt)) return cur;
      if (cur && cur.kind === 'skill' && skills.some((s) => s.name === cur.id && s.instructions)) return cur;
      if (writing) return { kind: 'agent', id: writing.agentId };
      if (firstAgent) return { kind: 'agent', id: firstAgent.agentId };
      return null;
    });
  }, [agents, skills, activeTaskId]);

  // Stay mounted whenever there's ANYTHING to show — a prompt, a phase, rows, or
  // we're live/awaiting. The caller (ChatWorkspace) owns the prompt here as the
  // single window, so collapsing on "empty" would drop the prompt back to a bare
  // bubble. Only return null when truly nothing exists to render.
  if (!live && !awaitingApproval && !promptText && !ba.phase
      && agents.length === 0 && skills.length === 0
      && !(typeof ba.reasoning === 'string' && ba.reasoning.length > 0)) return null;

  const doneCount = agents.filter((a) => a.phase === 'done').length;
  // Awaiting approval = paused mid-build (NOT done): show a paused label, not
  // "Network built" and not an active phase spinner.
  const phaseLabel = awaitingApproval
    ? 'Awaiting approval'
    : (live ? (PHASE_LABEL[ba.phase] || 'Building your network') : 'Network built');

  // Resolve the reader's content (prompt text + usage + a label).
  let readerBody = null;
  let readerUsage = null;
  let readerLabel = null;
  if (sel && sel.kind === 'agent') {
    const a = agents.find((x) => x.agentId === sel.id);
    if (a && a.prompt) { readerBody = a.prompt; readerUsage = a.usage; readerLabel = a.label || a.agentId; }
  } else if (sel && sel.kind === 'skill') {
    const s = skills.find((x) => x.name === sel.id);
    if (s && s.instructions) {
      readerBody = (s.whenToUse ? `When to use: ${s.whenToUse}\n\n` : '') + s.instructions;
      readerUsage = s.usage; readerLabel = s.name;
    }
  }
  // The Reader is tied to its SOURCE LANE: it only shows while that lane is open. When the Agents (or
  // Skills) lane auto-folds on completion, the reader for its selection folds too — otherwise the
  // body sits open showing a row that's now hidden (out of sync). Re-opening the lane + selecting a
  // row brings it back. (Loading text on select is the auto-select effect above.)
  const readerVisible = !!readerBody && (
    (sel && sel.kind === 'agent' && agentsOpen)
    || (sel && sel.kind === 'skill' && skillsOpen)
  );

  // Revision index for the header label (1-based, oldest=1).
  const revCount = revisions.length;
  const activeRevIdx = activeTaskId ? revisions.findIndex((r) => r.taskId === activeTaskId) : -1;
  const revNumber = activeRevIdx >= 0 ? revCount - activeRevIdx : revCount;

  return (
    <motion.div
      layout
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      className="min-w-0 overflow-hidden rounded-lg border border-gray-200 bg-white"
      data-testid="build-activity-panel"
    >
      {/* Header — phase / status, selected item, revision switcher. */}
      <div className="flex items-center gap-2 border-b border-gray-200 bg-white px-3 py-1.5">
        {awaitingApproval
          ? <PauseCircle className="h-4 w-4 text-amber-500" aria-hidden />
          : live
            ? <Loader2 className="h-4 w-4 animate-spin text-blue-500" aria-hidden />
            : <Check className="h-4 w-4 text-emerald-500" aria-hidden />}
        <span className="text-[0.8125rem] font-semibold text-gray-800">{phaseLabel}</span>
        {!live && revCount > 1 && (
          <span className="text-[0.75rem] text-gray-500">· rev {revNumber} of {revCount}</span>
        )}
        {readerLabel && (
          <span className="truncate text-[0.75rem] text-gray-400">· {readerLabel}</span>
        )}
        {/* Revision switcher — only when there's history and the build is settled. */}
        {!live && revCount > 1 && (
          <select
            value={activeTaskId || ''}
            onChange={(e) => selectRevision(e.target.value)}
            disabled={revLoading}
            className="ml-auto rounded border border-gray-200 bg-white px-1.5 py-0.5 text-[0.75rem] text-gray-600"
            data-testid="build-revision-select"
            title="View a previous build revision"
          >
            {revisions.map((r, i) => (
              <option key={r.taskId} value={r.taskId}>
                rev {revCount - i}{i === 0 ? ' (latest)' : ''}
              </option>
            ))}
          </select>
        )}
        {agents.length > 0 && (
          <span className={`${(!live && revCount > 1) ? '' : 'ml-auto'} shrink-0 text-[0.75rem] text-gray-400`}>
            {doneCount}/{agents.length} agents
          </span>
        )}
      </div>

      {/* Prompt — the request that kicked off this build, as the box's top
          section (room-worker style), so the whole build lives in one box. */}
      {promptText ? (
        <div className="border-b border-gray-200 bg-white">
          <button
            type="button"
            onClick={() => setPromptOpen((v) => !v)}
            className="flex w-full items-center gap-1.5 px-3 py-1.5 text-left"
          >
            {promptOpen ? <ChevronDown className="h-3.5 w-3.5 text-gray-400" /> : <ChevronRight className="h-3.5 w-3.5 text-gray-400" />}
            <span className="text-[0.75rem] font-medium uppercase tracking-wide text-gray-400">Prompt</span>
          </button>
          {promptOpen ? (
            <div className="px-3 pb-2">
              <div className="max-h-40 overflow-y-auto whitespace-pre-wrap text-[0.9375rem] leading-normal text-gray-700 [scrollbar-gutter:stable]">
                {promptText}
              </div>
              {promptTime ? (
                <div className="mt-1.5 text-[0.75rem] text-gray-400" title={formatMessageTimeFull(promptTime)}>{formatMessageTime(promptTime)}</div>
              ) : null}
            </div>
          ) : null}
        </div>
      ) : null}

      {/* Architect reasoning — streams live (keeps the user engaged while the
          architect thinks) and stays readable afterward (persisted BUILD_REASONING
          'done'). Auto-expanded while streaming; collapsible once settled. */}
      {(ba.reasoning && ba.reasoning.length > 0) ? (
        <ReasoningBlock text={ba.reasoning} streaming={live && !reasoningSettled} />
      ) : null}

      {/* Reader — the selected agent's prompt OR skill body (scroll + resize). Visible only while
          its source lane (Agents/Skills) is open, so it stays in sync with the lane. */}
      {readerVisible ? (
        <>
          <div
            style={{ height: `${height}px` }}
            className="min-w-0 overflow-y-auto overflow-x-hidden px-3 py-2 text-[0.9375rem] leading-relaxed text-gray-800 [scrollbar-gutter:stable] whitespace-pre-wrap"
          >
            {readerBody}
          </div>
          <UsageBar usage={readerUsage} />
          <div
            onMouseDown={onMouseDown}
            className="flex items-center justify-center h-2 cursor-row-resize border-t border-gray-200/80 bg-white hover:bg-gray-50 transition-colors group"
          >
            <GripHorizontal className="h-2.5 w-3.5 text-gray-400 group-hover:text-gray-600" />
          </div>
        </>
      ) : null}

      {/* Footer — architect lane, agent roster, then the Skills deck. Each lane
          carries its OWN usage chip (model · $ · pts). */}
      <div className="border-t border-gray-200 bg-white px-2 py-1.5">
        {/* Architect (design step, opus/reason tier) — its own lane + usage. */}
        {architectUsage && (
          <div className="mb-1.5 flex items-center gap-2 rounded px-1.5 py-1">
            <span className="flex h-4 w-4 shrink-0 items-center justify-center">
              <Sparkles className="h-4 w-4 text-amber-500" aria-hidden />
            </span>
            <span className="truncate text-[0.875rem] text-gray-700">Architect</span>
            <LaneUsage usage={architectUsage} fallback="" />
          </div>
        )}

        {agents.length > 0 && (
          <div className="space-y-0.5">
            <button
              type="button"
              onClick={() => setAgentsOpen((v) => !v)}
              className="mb-1 flex w-full items-center gap-1.5 text-left"
            >
              {agentsOpen ? <ChevronDown className="h-3.5 w-3.5 text-gray-400" /> : <ChevronRight className="h-3.5 w-3.5 text-gray-400" />}
              <span className="text-[0.8125rem] font-medium text-gray-600">Agents</span>
              <span className="text-[0.75rem] text-gray-400">{agents.length}</span>
              {agentsDone ? <span className="text-[0.75rem] text-gray-400">· done</span> : null}
            </button>
            {agentsOpen ? (
              <AnimatePresence initial={false}>
                {agents.map((row) => (
                  <AgentListRow
                    key={row.agentId}
                    row={row}
                    selected={sel && sel.kind === 'agent' && sel.id === row.agentId}
                    onSelect={selectAgent}
                  />
                ))}
              </AnimatePresence>
            ) : null}
          </div>
        )}

        {skills.length > 0 && (
          <div className="mt-2 border-t border-gray-200 pt-2">
            <button
              type="button"
              onClick={() => setSkillsOpen((v) => !v)}
              className="mb-1 flex w-full items-center gap-1.5 text-left"
            >
              {skillsOpen ? <ChevronDown className="h-3.5 w-3.5 text-gray-400" /> : <ChevronRight className="h-3.5 w-3.5 text-gray-400" />}
              <Sparkles className="h-3.5 w-3.5 text-violet-500" aria-hidden />
              <span className="text-[0.8125rem] font-medium text-gray-600">Skills</span>
              <span className="text-[0.75rem] text-gray-400">{skills.length}</span>
              {/* Skills are drafted in ONE LLM call, so cost is a GROUP total on
                  the header — not a per-skill chip (which would falsely imply
                  one skill cost everything and the rest were free). */}
              <LaneUsage usage={skillsUsage} fallback="" />
            </button>
            {skillsOpen ? (
              <div className="space-y-0.5">
                <AnimatePresence initial={false}>
                  {skills.map((row) => (
                    <SkillRow
                      key={row.name}
                      row={row}
                      selected={sel && sel.kind === 'skill' && sel.id === row.name}
                      onSelect={selectSkill}
                    />
                  ))}
                </AnimatePresence>
              </div>
            ) : null}
          </div>
        )}

        {/* ARCHITECTURE (PLAN-DIAGRAMS.md): the SHIP — the project's target-architecture diagram the
            architect drew. Rendered when the built manifest carries one. Scrollable (asks #2/#4): a
            large Mermaid diagram is not react-flow, so the body scrolls both axes with a height cap. */}
        {activeShip && (
          <div className="mt-2 border-t border-gray-200 pt-2">
            <button
              type="button"
              onClick={() => setArchOpen((v) => !v)}
              className="mb-1 flex w-full items-center gap-1.5 text-left"
              data-testid="build-architecture-toggle"
            >
              {archOpen ? <ChevronDown className="h-3.5 w-3.5 text-gray-400" />
                : <ChevronRight className="h-3.5 w-3.5 text-gray-400" />}
              <Network className="h-4 w-4 text-indigo-500" aria-hidden />
              <span className="text-[0.8125rem] font-medium text-gray-600">Architecture</span>
              <span className="text-[0.75rem] text-gray-400">
                {planKind === 'playbook' ? '· the target system'
                  : planKind === 'teams' ? '· the teams and what each owns' : '· suggested target system'}
              </span>
            </button>
            {archOpen && (
              <div
                className="max-h-[70vh] overflow-auto rounded-lg border border-gray-200 bg-white p-2"
                data-testid="build-architecture-diagram"
              >
                <MermaidDiagram code={activeShip} />
              </div>
            )}
          </div>
        )}

        {/* Playbook (project networks): the per-milestone plan groom — one row per milestone,
            each grooming its task-DAG in parallel with its OWN cost chip (per-milestone factory).
            Collapsible; auto-folded when the Architecture diagram is present (ask #1). */}
        {playbook.length > 0 && (
          <div className="mt-2 border-t border-gray-200 pt-2">
            <button
              type="button"
              onClick={() => setPlaybookOpen((v) => !v)}
              className="mb-1 flex w-full items-center gap-1.5 text-left"
              data-testid="build-playbook-toggle"
            >
              {playbookOpen ? <ChevronDown className="h-3.5 w-3.5 text-gray-400" />
                : <ChevronRight className="h-3.5 w-3.5 text-gray-400" />}
              <ListChecks className="h-3.5 w-3.5 text-sky-500" aria-hidden />
              <span className="text-[0.8125rem] font-medium text-gray-600">{plan.title}</span>
              <span className="text-[0.75rem] text-gray-400">
                {planKind === 'teams' ? `${playbook.length} team${playbook.length === 1 ? '' : 's'}`
                  : `${playbook.length} milestone${playbook.length === 1 ? '' : 's'}`} {plan.note}
              </span>
            </button>
            {playbookOpen && (
              <div className="space-y-0.5">
                <AnimatePresence initial={false}>
                  {playbook.map((row) => (
                    <PlaybookRow
                      key={row.milestoneId}
                      row={row}
                      expanded={isMilestoneExpanded(row)}
                      onToggle={() => toggleMilestone(row.milestoneId)}
                      unit={plan.unit}
                    />
                  ))}
                </AnimatePresence>
              </div>
            )}
          </div>
        )}
      </div>
    </motion.div>
  );
}
